import { useState } from "react"

/**
 * El RIF, en dos controles que componen una sola cadena.
 *
 * ⚠ **El 2.0 lo modela como dos columnas** —`tipo_documento` con un `ENUM('V','E','J','G','P')`
 * y `documento`— con un `UNIQUE` sobre el par. Aquí viaja compuesto, que es como se
 * lee y como se teclea, pero el formulario se parte igual: un campo de texto libre
 * deja escribir `j50000000`, `J50.000.000` y `J-50000000-1` como si fueran cosas
 * distintas, y entonces buscar un cliente por su RIF deja de encontrarlo.
 *
 * El servidor valida el mismo formato con un patrón. Esto no es la garantía: es lo
 * que evita que alguien llegue a enviarlo mal.
 */

/** `V` venezolano · `E` extranjero · `J` jurídico · `G` gobierno · `P` pasaporte. */
export const PREFIJOS_RIF = ["V", "E", "J", "G", "P"] as const

export type PrefijoRif = (typeof PREFIJOS_RIF)[number]

/**
 * Parte un RIF compuesto en sus dos mitades.
 *
 * Un valor que no encaje devuelve el prefijo por defecto y el resto tal cual: así
 * editar un cliente cuyo RIF vino del 2.0 con otra grafía no lo borra al abrirlo.
 */
export function partirRif(rif: string): { prefijo: PrefijoRif; numero: string } {
  const encaje = /^([VEJGP])-?(.*)$/i.exec(rif.trim())
  // Los grupos son `string | undefined` para TypeScript aunque el patrón garantice
  // los dos. Se resuelve con respaldos en vez de una aserción: una aserción aquí
  // sería afirmar algo del regex que el siguiente que lo edite no va a comprobar.
  const letra = encaje?.[1]
  if (letra === undefined) return { prefijo: "J", numero: rif.trim() }
  return {
    prefijo: letra.toUpperCase() as PrefijoRif,
    numero: encaje?.[2] ?? "",
  }
}

/** Los junta en la forma canónica `J-50000000-1`. Sin número, cadena vacía. */
export function componerRif(prefijo: PrefijoRif, numero: string): string {
  const limpio = numero.trim()
  return limpio === "" ? "" : `${prefijo}-${limpio}`
}

type Props = {
  id: string
  etiqueta?: string
  valor: string
  onCambiar: (rif: string) => void
}

export function CampoRif({ id, etiqueta = "RIF", valor, onCambiar }: Props) {
  const partido = partirRif(valor)
  /**
   * ⚠ El prefijo se recuerda aparte, y no es redundante.
   *
   * Sin número no hay RIF, así que `componerRif` devuelve cadena vacía — y de una
   * cadena vacía no se puede volver a leer el prefijo. Derivarlo sólo del valor hacía
   * que elegir «G» antes de teclear el número lo perdiera: el campo volvía a «J» y el
   * cliente se guardaba con el prefijo equivocado sin que nadie viera el salto.
   */
  const [prefijoElegido, setPrefijoElegido] = useState<PrefijoRif>(partido.prefijo)
  const prefijo = valor.trim() === "" ? prefijoElegido : partido.prefijo
  const numero = partido.numero

  const elegirPrefijo = (p: PrefijoRif) => {
    setPrefijoElegido(p)
    onCambiar(componerRif(p, numero))
  }

  return (
    <div>
      <span className="block text-sm" id={`${id}-etiqueta`}>
        {etiqueta}
      </span>
      <div className="mt-1 flex gap-2">
        <select
          aria-label={`Tipo de documento del ${etiqueta}`}
          className="campo w-20"
          value={prefijo}
          onChange={(e) => elegirPrefijo(e.target.value as PrefijoRif)}
        >
          {PREFIJOS_RIF.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <input
          id={id}
          aria-labelledby={`${id}-etiqueta`}
          inputMode="numeric"
          placeholder="50000000-1"
          className="campo flex-1 tabular-nums"
          value={numero}
          onChange={(e) => onCambiar(componerRif(prefijo, e.target.value))}
        />
      </div>
    </div>
  )
}
